import type { Locale } from "@/lib/i18n";
import type { FaqBlockItem } from "@/components/site/FaqBlock";

export type FaqBlockKey =
  | "forskola"
  | "forskoleklass"
  | "grundskola"
  | "avgifter"
  | "anmalan"
  | "om-skolan"
  | "hogstadium"
  | "fritids"
  | "personal"
  | "elevhalsa"
  | "extra-stod"
  | "arskurser"
  | "omdomen";

/**
 * Korta frågor och svar som visas längst ner på de tyngsta sidorna och
 * publiceras som FAQPage-schema. Svaren håller sig till sådant som redan
 * står på sajten – inga nya löften eller siffror.
 */
const FAQ: Record<FaqBlockKey, Record<Locale, FaqBlockItem[]>> = {
  forskola: {
    sv: [
      {
        q: "Vilka åldrar tar förskolan emot?",
        a: "Förskolan är för barn mellan 2 och 6 år. Barnen är i åldersblandade grupper med svensk läroplan som grund.",
      },
      {
        q: "Vilket språk talas i förskolan?",
        a: "Verksamheten är på svenska, och barnen möter spanska och engelska varje dag i lek och rutiner.",
      },
      {
        q: "Hur går inskolningen till?",
        a: "Inskolningen sker i barnets takt tillsammans med en vårdnadshavare. Vi planerar upplägget med er innan starten så att barnet hinner landa i gruppen.",
      },
      {
        q: "När kan vi börja?",
        a: "Platser tilldelas löpande och det går bra att börja mitt i terminen när det finns plats. Hör av er i god tid inför önskad termin.",
      },
    ],
    en: [
      {
        q: "What ages does the preschool take?",
        a: "The preschool is for children aged 2 to 6, in mixed-age groups based on the Swedish curriculum.",
      },
      {
        q: "Which language is spoken at preschool?",
        a: "The day is in Swedish, and the children meet Spanish and English every day through play and routines.",
      },
      {
        q: "How does settling in work?",
        a: "Settling in happens at the child's own pace together with a guardian. We plan it with you before the start so the child has time to feel at home.",
      },
      {
        q: "When can we start?",
        a: "Places are allocated on a rolling basis and starting mid-term is fine when a place is available. Get in touch in good time before your preferred term.",
      },
    ],
    es: [
      {
        q: "¿Qué edades acepta el preescolar?",
        a: "El preescolar es para niños de 2 a 6 años, en grupos de edades mixtas basados en el currículo sueco.",
      },
      {
        q: "¿Qué idioma se habla en el preescolar?",
        a: "La jornada es en sueco, y los niños tienen contacto con el español y el inglés cada día a través del juego y las rutinas.",
      },
      {
        q: "¿Cómo es la adaptación?",
        a: "La adaptación se hace al ritmo del niño y con un adulto de la familia. La planificamos con vosotros antes del inicio.",
      },
      {
        q: "¿Cuándo podemos empezar?",
        a: "Las plazas se asignan de forma continua y se puede empezar a mitad de trimestre si hay plaza. Contactadnos con tiempo antes del trimestre deseado.",
      },
    ],
  },
  forskoleklass: {
    sv: [
      {
        q: "Vilken ålder gäller för förskoleklass?",
        a: "Förskoleklassen är för barn som fyller 6 år under läsåret, alltså ungefär 5–6 år. Barn som redan går på vår förskola går vidare hit.",
      },
      {
        q: "Är förskoleklass obligatorisk?",
        a: "I Sverige är förskoleklassen obligatorisk från höstterminen det år barnet fyller 6 år. Vi följer svensk läroplan så att barnet ligger i fas med skolgången i Sverige.",
      },
      {
        q: "Hur skiljer sig förskoleklassen från förskolan?",
        a: "Dagen har mer struktur och mer medvetet arbete med bokstäver, siffror och samarbete, men leken finns kvar som grund. Barnen är med i skolans rutiner och gemenskap.",
      },
      {
        q: "Vilka språk används i förskoleklassen?",
        a: "Svenska är basen. Spanska och engelska finns med varje dag i lek, sång och samtal, precis som i resten av skolan.",
      },
      {
        q: "Hur gör vi för att söka plats?",
        a: "Platser tilldelas löpande och antalet är begränsat. Ansökan görs via ansökningsformuläret och avgifterna finns på avgiftssidan.",
      },
    ],
    en: [
      {
        q: "What age is the preschool class for?",
        a: "The preschool class is for children turning 6 during the school year, roughly ages 5–6. Children already at our preschool continue here.",
      },
      {
        q: "Is the preschool class compulsory?",
        a: "In Sweden the preschool class is compulsory from the autumn term of the year a child turns 6. We follow the Swedish curriculum so the child stays in step with schooling in Sweden.",
      },
      {
        q: "How does it differ from preschool?",
        a: "The day is more structured, with more deliberate work on letters, numbers and cooperation, while play remains the foundation. The children take part in the school's routines and community.",
      },
      {
        q: "Which languages are used?",
        a: "Swedish is the base. Spanish and English are part of every day through play, song and conversation, just as in the rest of the school.",
      },
      {
        q: "How do we apply for a place?",
        a: "Places are allocated on a rolling basis and numbers are limited. Apply through the application form; fees are listed on the fees page.",
      },
    ],
    es: [
      {
        q: "¿Para qué edad es la clase preescolar?",
        a: "La clase preescolar es para niños que cumplen 6 años durante el curso, aproximadamente 5–6 años. Los niños de nuestro preescolar continúan aquí.",
      },
      {
        q: "¿Es obligatoria la clase preescolar?",
        a: "En Suecia es obligatoria desde el trimestre de otoño del año en que el niño cumple 6 años. Seguimos el currículo sueco para que el niño esté al día con la escolarización en Suecia.",
      },
      {
        q: "¿En qué se diferencia del preescolar?",
        a: "La jornada está más estructurada, con más trabajo consciente de letras, números y cooperación, sin dejar de tener el juego como base. Los niños participan en las rutinas y la vida del colegio.",
      },
      {
        q: "¿Qué idiomas se usan?",
        a: "El sueco es la base. El español y el inglés están presentes cada día en el juego, las canciones y las conversaciones, igual que en el resto del colegio.",
      },
      {
        q: "¿Cómo se solicita plaza?",
        a: "Las plazas se asignan de forma continua y son limitadas. La solicitud se hace con el formulario y las tarifas están en la página de tarifas.",
      },
    ],
  },
  grundskola: {
    sv: [
      {
        q: "Vilka årskurser finns på skolan?",
        a: "Grundskolan omfattar förskoleklass till årskurs 6. Högstadiet och gymnasiet läses på distans via Sofia Distans och Hermods med stöd på plats.",
      },
      {
        q: "Hur stora är klasserna?",
         a: "Läsåret 2026/27 har vi högst 16 elever per klass.",
      },
      {
        q: "Följer ni svensk läroplan?",
         a: "Ja. Undervisningen följer svensk läroplan och svenska kursplaner. All svensk undervisning ges av lärare med svensk lärarlegitimation. Spanskan börjar i förskolan och finns med varje dag.",
      },
      {
        q: "Går det att börja mitt i terminen?",
        a: "Ja, ansökningar tas emot löpande och det går bra att börja mitt i terminen när det finns plats i årskursen.",
      },
    ],
    en: [
      {
        q: "Which year groups does the school cover?",
        a: "The school covers reception through year 6. Lower and upper secondary are studied at a distance via Sofia Distans and Hermods with on-site support.",
      },
      {
        q: "How large are the classes?",
         a: "In the 2026/27 school year, we have no more than 16 pupils per class.",
      },
      {
        q: "Do you follow the Swedish curriculum?",
         a: "Yes. Teaching follows the Swedish curriculum and syllabuses. All Swedish tuition is taught by teachers with Swedish teaching qualifications. Spanish starts in preschool and is on the timetable every day.",
      },
      {
        q: "Can we start mid-term?",
        a: "Yes. Applications are accepted on a rolling basis and starting mid-term is fine when there is a place in the year group.",
      },
    ],
    es: [
      {
        q: "¿Qué cursos ofrece el colegio?",
        a: "El colegio abarca desde infantil hasta 6.º de primaria. La secundaria y el bachillerato se cursan a distancia con Sofia Distans y Hermods, con apoyo presencial.",
      },
      {
        q: "¿De qué tamaño son los grupos?",
         a: "En el curso 2026/27 tenemos un máximo de 16 alumnos por clase.",
      },
      {
        q: "¿Seguís el currículo sueco?",
         a: "Sí. La enseñanza sigue el currículo y los programas suecos. Toda la enseñanza en sueco la imparten docentes con titulación docente sueca. El español empieza en Infantil y está en el horario cada día.",
      },
      {
        q: "¿Se puede empezar a mitad de trimestre?",
        a: "Sí. Aceptamos solicitudes de forma continua y se puede empezar a mitad de trimestre si hay plaza en el curso.",
      },
    ],
  },
  avgifter: {
    sv: [
      {
        q: "Vad ingår i terminsavgiften?",
        a: "Undervisning enligt svensk läroplan under skoldagen ingår. Tillval som fritids, fotbollsakademi och padel/tennis läggs till separat och syns direkt i anmälningsformuläret. Skolavgiften ska vara betald innan eleven börjar på skolan.",
      },
      {
        q: "Finns det syskonrabatt?",
        a: "Ja. Familjer med flera barn på skolan får rabatt, som räknas på den lägre avgiften.",
      },
      {
        q: "Hur fungerar korttidsplats?",
        a: "För familjer som är i Spanien delar av året finns korttidsplats med reducerad inskrivningsavgift och avgift efter vistelsens längd.",
      },
      {
        q: "Kan vi få statsbidrag?",
        a: "Familjer där en vårdnadshavare är utsänd i arbete kan i vissa fall ansöka om statsbidrag hos Skolverket för distansstudier på högstadie- och gymnasienivå. Villkoren avgörs av Skolverket.",
      },
    ],
    en: [
      {
        q: "What is included in the term fee?",
        a: "Teaching according to the Swedish curriculum during the school day is included. Options such as after-school club, the football academy and padel/tennis are added separately and are shown live in the application form. The school fee must be paid before the pupil starts at the school.",
      },
      {
        q: "Is there a sibling discount?",
        a: "Yes. Families with several children at the school receive a discount, calculated on the lower fee.",
      },
      {
        q: "How does short-term enrolment work?",
        a: "For families who spend part of the year in Spain there is short-term enrolment with a reduced registration fee and a fee based on the length of the stay.",
      },
      {
        q: "Can we get a state grant?",
        a: "Families where a guardian is posted abroad for work can in some cases apply to the Swedish National Agency for Education for a grant towards distance study at secondary level. The conditions are set by the agency.",
      },
    ],
    es: [
      {
        q: "¿Qué incluye la tarifa trimestral?",
        a: "Incluye la enseñanza según el currículo sueco durante la jornada escolar. Los servicios opcionales, como actividades extraescolares, academia de fútbol y pádel/tenis, se añaden aparte y se ven al instante en el formulario de solicitud. La tarifa escolar debe estar abonada antes de que el alumno empiece en el colegio.",
      },
      {
        q: "¿Hay descuento para hermanos?",
        a: "Sí. Las familias con varios hijos en el colegio reciben un descuento, calculado sobre la tarifa más baja.",
      },
      {
        q: "¿Cómo funciona la matrícula de estancia corta?",
        a: "Para familias que pasan solo parte del año en España existe la matrícula de estancia corta, con una cuota de matrícula reducida y una tarifa según la duración.",
      },
      {
        q: "¿Podemos recibir una ayuda estatal?",
        a: "Las familias en las que un progenitor está destinado por trabajo pueden en algunos casos solicitar una ayuda a la Agencia Nacional Sueca de Educación para los estudios a distancia de secundaria. Las condiciones las decide la agencia.",
      },
    ],
  },
  anmalan: {
    sv: [
      {
        q: "Är ansökan bindande?",
        a: "Nej. Ansökan är en intresseanmälan om plats – vi återkommer med besked och går igenom valen tillsammans med er innan skolstart.",
      },
      {
        q: "Hur lång tid tar formuläret?",
        a: "Ungefär fem minuter. Det du fyller i sparas löpande i din webbläsare, så du kan pausa och fortsätta senare.",
      },
      {
        q: "Vad händer efter att vi skickat in ansökan?",
        a: "Vi går igenom ansökan manuellt. Skolan hör av sig för besök eller samtal, nästa steg och besked om plats och startdatum – ni behöver inte boka något själva.",
      },
      {
        q: "Hur lång tid tar det att få besked?",
        a: "Vi går igenom varje ansökan manuellt och återkommer med besked om plats och startdatum. Har ni inte hört något från oss är det bara att höra av er, så kollar vi upp det.",
        links: [{ label: "Kontakta oss", href: "/kontakt" }],
      },
      {
        q: "Kan vi ansöka mitt under terminen?",
        a: "Ja. Ansökningar tas emot löpande och det går bra att börja mitt i terminen i mån av plats. I ansökan väljer ni önskad termin och startdatum, och datumet kontrolleras mot läsårskalendern.",
        links: [{ label: "Se läsårstider", href: "/lasarstider" }],
      },
      {
        q: "Vad gör vi om barnet saknar personnummer?",
        a: "Fyll i födelsedatum och passuppgifter i stället. NIE och folkbokföring ordnas ofta i samband med flytten och behöver inte vara klart vid ansökan.",
        links: [{ label: "Om NIE och folkbokföring", href: "/nie-och-folkbokforing-i-spanien" }],
      },
      {
        q: "Kan vi ansöka om plats för flera barn samtidigt?",
        a: "Ja. Flera barn kan sökas i samma formulär, och ni kan lägga till en eller två vårdnadshavare.",
      },
      {
        q: "Kan vi lägga till ett barn efter att ansökan skickats in?",
        a: "Ja. Hör av er så kompletterar vi er ansökan med syskonet – ni behöver inte fylla i en ny ansökan från början.",
        links: [{ label: "Kontakta oss", href: "/kontakt" }],
      },
      {
        q: "Kan vi ändra något i en redan inskickad ansökan?",
        a: "Ja. Kontakta oss med det som ska ändras, till exempel årskurs, startdatum, kontaktuppgifter eller val av idrott, så uppdaterar vi ansökan åt er.",
        links: [{ label: "Kontakta oss", href: "/kontakt" }],
      },
    ],
    en: [
      {
        q: "Is the application binding?",
        a: "No. The application registers your interest in a place — we come back with a decision and go through the choices with you before school starts.",
      },
      {
        q: "How long does the form take?",
        a: "About five minutes. What you enter is saved continuously in your browser, so you can pause and continue later.",
      },
      {
        q: "What happens after we submit?",
        a: "We review the application manually. The school gets in touch about a visit or a conversation, the next steps and the decision about a place and a start date — you do not need to book anything yourself.",
      },
      {
        q: "How long does it take to hear back?",
        a: "We review every application manually and get back to you with a decision and a start date. If you haven't heard from us, just get in touch and we will check.",
        links: [{ label: "Contact us", href: "/en/contact-us" }],
      },
      {
        q: "Can we apply during the term?",
        a: "Yes. Applications are accepted on a rolling basis and starting mid-term is fine when a place is available. In the form you choose the term and start date, and the date is checked against the school calendar.",
        links: [{ label: "School year dates", href: "/en/school-year-dates" }],
      },
      {
        q: "What if the child has no Swedish personal ID number?",
        a: "Enter the date of birth and passport details instead. NIE and local registration are usually arranged in connection with the move and do not need to be in place when you apply.",
        links: [{ label: "About NIE and registration", href: "/en/nie-and-registration-in-spain" }],
      },
      {
        q: "Can we apply for several children at once?",
        a: "Yes. Several children can be included in the same form, and you can add one or two guardians.",
      },
      {
        q: "Can we add another child after we have applied?",
        a: "Yes. Get in touch and we will add the sibling to your existing application — you do not need to start a new form.",
        links: [{ label: "Contact us", href: "/en/contact-us" }],
      },
      {
        q: "Can we change something in an application we have already sent?",
        a: "Yes. Contact us with what needs changing — year group, start date, contact details or sport choices — and we will update the application for you.",
        links: [{ label: "Contact us", href: "/en/contact-us" }],
      },
    ],
    es: [
      {
        q: "¿La solicitud es vinculante?",
        a: "No. La solicitud registra vuestro interés en una plaza: os responderemos con la decisión y repasaremos las opciones con vosotros antes del inicio.",
      },
      {
        q: "¿Cuánto se tarda en rellenar el formulario?",
        a: "Unos cinco minutos. Lo que escribís se guarda de forma continua en el navegador, así que podéis pausar y continuar más tarde.",
      },
      {
        q: "¿Qué ocurre después de enviar la solicitud?",
        a: "Revisamos la solicitud de forma manual. El colegio se pone en contacto para la visita o una conversación, los siguientes pasos y la respuesta sobre la plaza y la fecha de inicio: no tenéis que reservar nada por vuestra cuenta.",
      },
      {
        q: "¿Cuánto se tarda en recibir respuesta?",
        a: "Revisamos cada solicitud de forma manual y os respondemos con la decisión sobre la plaza y la fecha de inicio. Si no habéis tenido noticias, escribidnos y lo comprobamos.",
        links: [{ label: "Contactar", href: "/es/contacto" }],
      },
      {
        q: "¿Podemos solicitar plaza a mitad de trimestre?",
        a: "Sí. La admisión es continua y se puede empezar a mitad de trimestre si hay plaza. En el formulario elegís el trimestre y la fecha de inicio, y la fecha se comprueba con el calendario escolar.",
        links: [{ label: "Calendario escolar", href: "/es/calendario-escolar" }],
      },
      {
        q: "¿Y si el niño no tiene número de identidad sueco?",
        a: "Indicad la fecha de nacimiento y los datos del pasaporte. El NIE y el empadronamiento suelen tramitarse con la mudanza y no hacen falta al solicitar plaza.",
        
      },
      {
        q: "¿Podemos solicitar plaza para varios hijos a la vez?",
        a: "Sí. Podéis incluir varios hijos en el mismo formulario y añadir uno o dos tutores.",
      },
      {
        q: "¿Podemos añadir otro hijo después de enviar la solicitud?",
        a: "Sí. Escribidnos y añadimos al hermano a vuestra solicitud: no hace falta rellenar un formulario nuevo.",
        links: [{ label: "Contactar", href: "/es/contacto" }],
      },
      {
        q: "¿Podemos cambiar algo de una solicitud ya enviada?",
        a: "Sí. Contactadnos con lo que hay que cambiar (curso, fecha de inicio, datos de contacto o las actividades deportivas) y actualizamos la solicitud.",
        links: [{ label: "Contactar", href: "/es/contacto" }],
      },
    ],
  },

  "om-skolan": {
    sv: [
       { q: "Vem driver skolan och vilken läroplan följs?", a: "Skolan i Estepona följer svensk läroplan och svenska kursplaner. All svensk undervisning ges av lärare med svensk lärarlegitimation. Spanskan börjar i förskolan och finns med varje dag." },
      { q: "Hur stor är skolan?", a: "Vi är en liten skola med små grupper. Det gör att personalen känner alla elever och att vi snabbt kan justera när något behöver ändras." },
      { q: "Vilka årskurser finns på plats?", a: "Förskola från 3 år samt förskoleklass till och med årskurs 6 finns på plats. Högstadium och gymnasium läses på distans med handledare hos oss." },
      { q: "Går det att besöka skolan innan vi bestämmer oss?", a: "Ja. Ni är välkomna på ett besök på plats eller ett möte online innan ni bestämmer er." },
    ],
    en: [
       { q: "Who runs the school and which curriculum is followed?", a: "The school in Estepona follows the Swedish curriculum and syllabuses. All Swedish tuition is taught by teachers with Swedish teaching qualifications. Spanish starts in preschool and is on the timetable every day." },
      { q: "How big is the school?", a: "We are a small school with small groups. Staff know every pupil, and we can adjust quickly when something needs to change." },
      { q: "Which year groups are on site?", a: "Preschool from age 3 and reception through year 6 are on site. Lower and upper secondary are studied at a distance with supervisors here." },
      { q: "Can we visit before deciding?", a: "Yes. You are welcome to visit us on site or meet us online before you decide." },
    ],
    es: [
       { q: "¿Quién gestiona el colegio y qué currículo sigue?", a: "El colegio de Estepona sigue el currículo y los programas suecos. Toda la enseñanza en sueco la imparten docentes con titulación docente sueca. El español empieza en Infantil y está en el horario cada día." },
      { q: "¿Qué tamaño tiene el colegio?", a: "Somos un colegio pequeño con grupos reducidos. El personal conoce a cada alumno y podemos ajustar con rapidez cuando hace falta." },
      { q: "¿Qué cursos hay presenciales?", a: "Infantil desde los 3 años y desde preescolar hasta 6.º de primaria son presenciales. Secundaria y bachillerato se cursan a distancia con apoyo presencial." },
      { q: "¿Podemos visitar el colegio antes de decidir?", a: "Sí. Podéis visitarnos en persona o reunirnos en línea antes de decidir." },
    ],
  },
  hogstadium: {
    sv: [
      { q: "Hur fungerar högstadiet och gymnasiet hos er?", a: "Eleverna läser på distans via Sofia Distans (högstadiet) respektive Hermods (gymnasiet) och arbetar i våra lokaler med handledare på plats. Distansskolans lärare ansvarar för undervisning, bedömning och betyg.",
      links: [
        { label: "Sofia Distans", href: "https://sofiadistans.stockholm.se/" },
        { label: "Hermods Distansgymnasium", href: "https://hermods.se/gymnasium/" },
      ], },
      { q: "Hur många ansökningar behöver vi göra?", a: "Två: en till distansskolan och en till oss. Det innebär också två avgifter – avgiften till oss avser lokal, handledning och aktiviteter." },
      { q: "Kan vi få statsbidrag?", a: "Familjer där eleven medföljer en vårdnadshavare som är utsänd i arbete kan i vissa fall ansöka om statsbidrag hos Skolverket. Villkoren avgörs av Skolverket." },
      { q: "Går det att kombinera med idrott?", a: "Ja. Många äldre elever kombinerar studierna med fotbollsakademin eller padel- och tennisprofilen, och schemat läggs så att träning och studier går ihop." },
    ],
    en: [
      { q: "How do lower and upper secondary work here?", a: "Pupils study at a distance via Sofia Distans (lower secondary) and Hermods (upper secondary) and work in our premises with a supervisor on site. The distance school's teachers are responsible for teaching, assessment and grades.",
      links: [
        { label: "Sofia Distans", href: "https://sofiadistans.stockholm.se/" },
        { label: "Hermods Distansgymnasium", href: "https://hermods.se/gymnasium/" },
      ], },
      { q: "How many applications do we need to make?", a: "Two: one to the distance school and one to us. That also means two fees – our fee covers premises, supervision and activities." },
      { q: "Can we get a state grant?", a: "Families where the pupil accompanies a guardian posted abroad for work can in some cases apply to the Swedish National Agency for Education. The conditions are set by the agency." },
      { q: "Can studies be combined with sport?", a: "Yes. Many older pupils combine studies with the football academy or the padel and tennis profile, and the timetable is arranged so training and studies fit together." },
    ],
    es: [
      { q: "¿Cómo funcionan la secundaria y el bachillerato?", a: "El alumnado estudia a distancia con Sofia Distans (secundaria) y Hermods (bachillerato) y trabaja en nuestras instalaciones con un tutor presente. El profesorado de la escuela a distancia se encarga de la enseñanza, la evaluación y las notas.",
      links: [
        { label: "Sofia Distans", href: "https://sofiadistans.stockholm.se/" },
        { label: "Hermods Distansgymnasium", href: "https://hermods.se/gymnasium/" },
      ], },
      { q: "¿Cuántas solicitudes hay que hacer?", a: "Dos: una a la escuela a distancia y otra a nosotros. Eso implica también dos cuotas: la nuestra cubre instalaciones, tutoría y actividades." },
      { q: "¿Podemos recibir una ayuda estatal?", a: "Las familias cuyo hijo acompaña a un progenitor destinado por trabajo pueden en algunos casos solicitar ayuda a la Agencia Nacional Sueca de Educación. Las condiciones las decide la agencia." },
      { q: "¿Se puede combinar con el deporte?", a: "Sí. Muchos alumnos mayores combinan los estudios con la academia de fútbol o el programa de pádel y tenis, y el horario se organiza para que encaje." },
    ],
  },
  fritids: {
    sv: [
      { q: "Vilka tider har fritids?", a: "Fritids tar vid efter skoldagen och pågår till 16.30 under ordinarie skoldagar." },
      { q: "Vad gör barnen på fritids?", a: "Lek, skapande, utevistelse och rörelse i lugnare tempo, med personal som känner barnen från skoldagen." },
      { q: "Är fritids ett tillval?", a: "Ja. Fritids ligger utanför terminsavgiften och väljs till i ansökan – valet syns direkt i formuläret." },
      { q: "Kan vi ändra oss under terminen?", a: "Hör av er, så går vi igenom vad som är möjligt utifrån platser och terminens upplägg." },
    ],
    en: [
      { q: "What are the after-school hours?", a: "After-school care starts when the school day ends and runs until 4:30pm on regular school days." },
      { q: "What do the children do?", a: "Play, creative activities, outdoor time and movement at a calmer pace, with staff the children already know from the school day." },
      { q: "Is after-school care an option?", a: "Yes. It sits outside the term fee and is added in the application – the choice is shown in the form." },
      { q: "Can we change during the term?", a: "Get in touch and we will go through what is possible given places and the term's setup." },
    ],
    es: [
      { q: "¿Qué horario tienen las actividades extraescolares?", a: "Empiezan al terminar la jornada escolar y siguen hasta las 16:30 los días lectivos ordinarios." },
      { q: "¿Qué hacen los niños?", a: "Juego, actividades creativas, tiempo al aire libre y movimiento a un ritmo tranquilo, con personal que ya conocen." },
      { q: "¿Es un servicio opcional?", a: "Sí. Está fuera de la tarifa trimestral y se añade en la solicitud; la elección se ve en el formulario." },
      { q: "¿Podemos cambiar durante el trimestre?", a: "Escribidnos y repasamos qué es posible según las plazas y la organización del trimestre." },
    ],
  },
  personal: {
    sv: [
       { q: "Är lärarna svenska och legitimerade?", a: "All svensk undervisning ges av lärare med svensk lärarlegitimation. Spanskan börjar i förskolan och finns med varje dag. Se personalens individuella meriter på den här sidan." },
      { q: "Hur nära är kontakten med lärarna?", a: "Nära. I en liten skola träffas vi ofta i vardagen, och vårdnadshavare kan enkelt följa hur det går för sitt barn." },
      { q: "Vem leder skolan?", a: "Rektor Maria Wikström, som är lärare och specialpedagog och har lång erfarenhet som rektor i Sverige." },
      { q: "Hur får vi tag på personalen?", a: "Via skolans kontaktuppgifter, eller direkt i vardagen vid lämning och hämtning." },
    ],
    en: [
       { q: "Are the teachers Swedish and certified?", a: "All Swedish tuition is taught by teachers with Swedish teaching qualifications. Spanish starts in preschool and is on the timetable every day. Individual qualifications are listed on this page." },
      { q: "How close is contact with the teachers?", a: "Close. In a small school we meet often in everyday life, and guardians can easily follow how their child is doing." },
      { q: "Who leads the school?", a: "Principal Maria Wikström, a teacher and special needs educator with long experience as a principal in Sweden." },
      { q: "How do we reach the staff?", a: "Through the school's contact details, or directly at drop-off and pick-up." },
    ],
    es: [
       { q: "¿El profesorado es sueco y titulado?", a: "Toda la enseñanza en sueco la imparten docentes con titulación docente sueca. El español empieza en Infantil y está en el horario cada día. Las titulaciones individuales figuran en esta página." },
      { q: "¿Cómo es el contacto con el profesorado?", a: "Cercano. En un colegio pequeño coincidimos a diario y las familias pueden seguir con facilidad cómo va su hijo." },
      { q: "¿Quién dirige el colegio?", a: "La directora Maria Wikström, profesora y pedagoga terapéutica con larga experiencia como directora en Suecia." },
      { q: "¿Cómo contactamos con el personal?", a: "A través de los datos de contacto del colegio o directamente en la entrada y la salida." },
    ],
  },
  elevhalsa: {
    sv: [
      { q: "Vad gör elevhälsoteamet?", a: "Teamet arbetar förebyggande och hälsofrämjande tillsammans med lärarna, och stöttar när en elev behöver något extra – i lärandet eller i trivseln." },
      { q: "Hur kommer vi i kontakt med teamet?", a: "Prata med klassläraren eller rektorn, så kopplas rätt person in." },
      { q: "Hur arbetar ni med trygghet?", a: "Vi arbetar för att varje elev ska bli sedd, både i klassrummet och på rasterna, och tar upp saker tidigt tillsammans med vårdnadshavarna." },
      { q: "Hur hanteras känsliga uppgifter?", a: "Uppgifter om enskilda elever hanteras varsamt och delas bara med dem som behöver dem i sitt arbete." },
    ],
    en: [
      { q: "What does the student health team do?", a: "The team works preventively and health-promotingly alongside the teachers, and supports pupils who need something extra – in learning or wellbeing." },
      { q: "How do we get in touch with the team?", a: "Speak to the class teacher or the principal and the right person will be involved." },
      { q: "How do you work with safety and wellbeing?", a: "We work so that every pupil is seen, in the classroom and during breaks, and raise things early together with guardians." },
      { q: "How is sensitive information handled?", a: "Information about individual pupils is handled carefully and shared only with those who need it for their work." },
    ],
    es: [
      { q: "¿Qué hace el equipo de salud escolar?", a: "Trabaja de forma preventiva junto al profesorado y apoya al alumnado que necesita algo más, en el aprendizaje o en el bienestar." },
      { q: "¿Cómo contactamos con el equipo?", a: "Hablad con el tutor o con la directora y se implicará a la persona adecuada." },
      { q: "¿Cómo trabajáis la seguridad y el bienestar?", a: "Procuramos que cada alumno se sienta visto, en clase y en los recreos, y abordamos las cosas pronto junto con las familias." },
      { q: "¿Cómo se tratan los datos sensibles?", a: "La información sobre cada alumno se trata con cuidado y solo se comparte con quien la necesita para su trabajo." },
    ],
  },
  "extra-stod": {
    sv: [
      { q: "Hur upptäcks behov av extra stöd?", a: "Genom den dagliga undervisningen och uppföljningen. I små grupper märks det tidigt om något behöver anpassas." },
      { q: "Vilket stöd kan skolan ge?", a: "Anpassningar i undervisningen, tydligare struktur och extra tid tillsammans med specialpedagogisk kompetens i personalen." },
      { q: "Är vårdnadshavarna med i planeringen?", a: "Ja. Vi går igenom upplägget tillsammans med er och följer upp hur det fungerar." },
      { q: "Kan skolan ta emot alla behov?", a: "Vi går alltid igenom förutsättningarna med er innan start, så att ni vet vad vi kan erbjuda i vår verksamhet." },
    ],
    en: [
      { q: "How are needs for extra support identified?", a: "Through everyday teaching and follow-up. In small groups it shows early when something needs adapting." },
      { q: "What support can the school give?", a: "Adaptations in teaching, clearer structure and extra time, together with special needs expertise among the staff." },
      { q: "Are guardians involved in the planning?", a: "Yes. We go through the setup with you and follow up on how it works." },
      { q: "Can the school meet every need?", a: "We always go through the conditions with you before the start so you know what we can offer." },
    ],
    es: [
      { q: "¿Cómo se detectan las necesidades de apoyo?", a: "A través de la enseñanza diaria y el seguimiento. En grupos reducidos se nota pronto si algo hay que adaptar." },
      { q: "¿Qué apoyo puede dar el colegio?", a: "Adaptaciones en la enseñanza, una estructura más clara y tiempo extra, con competencia en pedagogía terapéutica en el equipo." },
      { q: "¿Participan las familias en la planificación?", a: "Sí. Repasamos el plan con vosotros y hacemos seguimiento de cómo funciona." },
      { q: "¿Puede el colegio atender cualquier necesidad?", a: "Siempre revisamos las condiciones con vosotros antes de empezar para que sepáis qué podemos ofrecer." },
    ],
  },
  arskurser: {
    sv: [
      { q: "Vilka årskurser finns på plats?", a: "Förskoleklass till och med årskurs 6 finns på plats, med förskola från 3 år. Från årskurs 7 sker studierna på distans med handledare hos oss.",
      links: [
        { label: "Sofia Distans", href: "https://sofiadistans.stockholm.se/" },
        { label: "Hermods Distansgymnasium", href: "https://hermods.se/gymnasium/" },
      ], },
      { q: "Hur ser skoldagen ut?", a: "Skoldagen är 9–15, med raster utomhus och rörelse. Efter skoldagen finns fritids till 16.30." },
      { q: "Arbetar ni åldersintegrerat?", a: "Vi har små grupper och arbetar åldersintegrerat i vissa aktiviteter, samtidigt som varje elev följer sin egen årskurs kursplan." },
      { q: "Hur följs kunskaperna upp?", a: "Med skriftliga omdömen i varje årskurs och löpande kontakt med vårdnadshavarna." },
    ],
    en: [
      { q: "Which year groups are on site?", a: "Reception through year 6 are on site, with preschool from age 3. From year 7 studies take place at a distance with supervisors here.",
      links: [
        { label: "Sofia Distans", href: "https://sofiadistans.stockholm.se/" },
        { label: "Hermods Distansgymnasium", href: "https://hermods.se/gymnasium/" },
      ], },
      { q: "What does the school day look like?", a: "The school day runs 9am–3pm with outdoor breaks and movement. After school there is after-school care until 4:30pm." },
      { q: "Do you work across age groups?", a: "We have small groups and mix ages in some activities, while every pupil follows the syllabus for their own year group." },
      { q: "How is progress followed up?", a: "With written assessments in every year group and continuous contact with guardians." },
    ],
    es: [
      { q: "¿Qué cursos hay presenciales?", a: "Desde preescolar hasta 6.º de primaria, con infantil desde los 3 años. A partir de 7.º los estudios son a distancia con apoyo presencial.",
      links: [
        { label: "Sofia Distans", href: "https://sofiadistans.stockholm.se/" },
        { label: "Hermods Distansgymnasium", href: "https://hermods.se/gymnasium/" },
      ], },
      { q: "¿Cómo es la jornada escolar?", a: "De 9:00 a 15:00, con recreos al aire libre y movimiento. Después hay actividades extraescolares hasta las 16:30." },
      { q: "¿Trabajáis con grupos de distintas edades?", a: "Tenemos grupos reducidos y mezclamos edades en algunas actividades, aunque cada alumno sigue el programa de su curso." },
      { q: "¿Cómo se hace el seguimiento?", a: "Con valoraciones escritas en cada curso y contacto continuo con las familias." },
    ],
  },
  omdomen: {
    sv: [
      { q: "Varifrån kommer omdömena?", a: "Från familjer på skolan som själva har skrivit och godkänt att vi publicerar det de säger. Källa och datum anges när de finns." },
      { q: "Redigerar ni texterna?", a: "Nej. Omdömena publiceras som familjerna skrivit dem." },
      { q: "Kan vi lämna ett eget omdöme?", a: "Ja. Hör av er så skickar vi en länk där ni kan skriva och godkänna publicering." },
      { q: "Vad betyder märkningen TEST?", a: "Den visar att exemplet är ett demoexempel och inte ett riktigt omdöme från en familj." },
    ],
    en: [
      { q: "Where do the reviews come from?", a: "From families at the school who have written them themselves and approved publication. Source and date are shown where available." },
      { q: "Do you edit the texts?", a: "No. Reviews are published as the families wrote them." },
      { q: "Can we leave our own review?", a: "Yes. Get in touch and we will send a link where you can write and approve publication." },
      { q: "What does the TEST label mean?", a: "It marks a demo example rather than a real review from a family." },
    ],
    es: [
      { q: "¿De dónde vienen las opiniones?", a: "De familias del colegio que las han escrito y han autorizado su publicación. Indicamos la fuente y la fecha cuando existen." },
      { q: "¿Editáis los textos?", a: "No. Las opiniones se publican tal y como las escribieron las familias." },
      { q: "¿Podemos dejar nuestra opinión?", a: "Sí. Escribidnos y os enviamos un enlace para redactarla y autorizar su publicación." },
      { q: "¿Qué significa la etiqueta TEST?", a: "Indica que es un ejemplo de demostración y no una opinión real de una familia." },
    ],
  },
};

export function faqFor(key: FaqBlockKey, locale: Locale): FaqBlockItem[] {
  return FAQ[key][locale];
}
